import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, Outlet, useMatch } from "react-router-dom";
import workspaceLogo from "../../assets/icons/svg/onbording/workspace.svg";
import bottomlogo from "../../assets/icons/svg/Cluva/logo.svg";
import { Check } from "lucide-react";

import HomeScreen from "./home_screns/HomeScreen";

import { API_URLS, BASE_URL, getAccessToken } from "../../api/urls";
import { ReactComponent as HomeIcon } from "../../assets/icons/svg/dashboardhome.svg";
import { ReactComponent as DocIcon } from "../../assets/icons/svg/dashboarddoc.svg";
import { ReactComponent as NotificationIcon } from "../../assets/icons/svg/dashboardnoti.svg";
import { ReactComponent as SettingIcon } from "../../assets/icons/svg/dashboardsettings.svg";
import { EnhancedTokenManager } from "../../utils/tokenManager";
import { SubdomainManager } from "../../utils/subdomainUtils";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import NotificationPopup from "./notifications_tab/NotificationPopup";
import { useWorkspaceIcon } from "../../contexts/WorkspaceIconContext";

import IconPNG1 from "../../assets/icons/png/onboarding-icon1.png";
import IconPNG2 from "../../assets/icons/png/onboarding-icon2.png";
import IconPNG3 from "../../assets/icons/png/onboarding-icon3.png";
import IconPNG4 from "../../assets/icons/png/onboarding-icon4.png";

interface DashboardScreenProps {
  hideSidebar?: boolean;
}

const Shimmer = ({ className }: { className: string }) => (
  <div
    className={`animate-pulse ${className}`}
    style={{
      background:
        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 2s ease-in-out infinite",
    }}
  />
);

const WorkspaceTooltipShimmer = () => (
  <div>
    <style>
      {`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}
    </style>
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-4 min-w-[280px]">
      <Shimmer className="h-3 w-20 rounded mb-3" />
      <div className="border-t border-[#EFF0F5] mb-3"></div>
      <div className="flex items-center justify-between mb-2">
        <Shimmer className="h-6 w-40 rounded" />
      </div>
      <Shimmer className="h-3 w-32 rounded" />
    </div>
  </div>
);

const mapIconUrlToLocalAsset = (iconUrl: string): string => {
  const filename = iconUrl.split("/").pop()?.toLowerCase() || "";
  const iconMapping: Record<string, string> = {
    "onboarding-icon1.png": IconPNG1,
    "onboarding-icon2.png": IconPNG2,
    "onboarding-icon3.png": IconPNG3,
    "onboarding-icon4.png": IconPNG4,
  };

  if (iconMapping[filename]) {
    console.log(`✅ Mapped ${filename} to local asset`);
    return iconMapping[filename];
  }
  return iconUrl;
};

const fetchWorkspaceIcon = async (
  workspaceId: number,
  bustCache: boolean = false,
): Promise<string> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      console.warn("No access token available for icon fetch");
      return workspaceLogo;
    }

    console.log(
      `📡 Fetching workspace icon for workspace ID: ${workspaceId}${
        bustCache ? " (cache busted)" : ""
      }`,
    );

    const iconUrl = bustCache
      ? `${API_URLS.GET_ICON(workspaceId)}?t=${Date.now()}`
      : API_URLS.GET_ICON(workspaceId);

    const iconResponse = await fetch(iconUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      ...(bustCache && { cache: "no-store" }),
    });

    if (!iconResponse.ok) {
      console.warn(`Failed to fetch workspace icon: ${iconResponse.status}`);
      return workspaceLogo;
    }

    const contentType = iconResponse.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      const jsonData = await iconResponse.json();
      const iconUrlPath = jsonData.data?.icon_url || jsonData.icon_url;

      if (iconUrlPath) {
        if (iconUrlPath.includes("onboarding-icon")) {
          const localIcon = mapIconUrlToLocalAsset(iconUrlPath);
          return localIcon;
        }

        const imageUrl = iconUrlPath.startsWith("http")
          ? iconUrlPath
          : `${BASE_URL}${
              iconUrlPath.startsWith("/") ? "" : "/"
            }${iconUrlPath}`;

        const finalImageUrl = bustCache
          ? `${imageUrl}?t=${Date.now()}`
          : imageUrl;

        const imageResponse = await fetch(finalImageUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          ...(bustCache && { cache: "no-store" }),
        });

        if (imageResponse.ok) {
          const imageBlob = await imageResponse.blob();
          if (imageBlob.size > 0 && imageBlob.type.startsWith("image/")) {
            const blobUrl = URL.createObjectURL(imageBlob);
            return blobUrl;
          }
        }
      }
    } else if (contentType?.startsWith("image/")) {
      const iconBlob = await iconResponse.blob();
      if (iconBlob.size > 0 && iconBlob.type.startsWith("image/")) {
        const blobUrl = URL.createObjectURL(iconBlob);
        return blobUrl;
      }
    }

    return workspaceLogo;
  } catch (error) {
    console.error("❌ Error fetching workspace icon:", error);
    return workspaceLogo;
  }
};

const DashboardScreen: React.FC<DashboardScreenProps> = ({
  hideSidebar = false,
}) => {
  useDocumentTitle("Dashboard");
  const navigate = useNavigate();
  const location = useLocation();

  // Determine selected tab based on URL
  const getSelectedTab = () => {
    const path = location.pathname;
    if (path.includes("/settings")) return "settings";
    if (path.includes("/notifications")) return "notifications";
    return "home";
  };

  const [selected, setSelected] = useState(getSelectedTab());
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [workspaceIconUrl, setWorkspaceIconUrl] = useState<string>("");
  const [homeResetTrigger, setHomeResetTrigger] = useState(0);
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [workspaceSubdomain, setWorkspaceSubdomain] = useState<string>("");
  const [workspaceDataLoaded, setWorkspaceDataLoaded] = useState(false);
  const [iconLoadError, setIconLoadError] = useState(false);
  const [iconLoading, setIconLoading] = useState(true);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [pendingNotificationEvent, setPendingNotificationEvent] = useState<{
    type: "shared" | "created";
    data: any;
  } | null>(null);

  const [showNotificationDot, setShowNotificationDot] = useState(false);
  const [pendingNotificationCardIds, setPendingNotificationCardIds] = useState<
    Set<number>
  >(new Set());

  const { iconUpdateTrigger, immediateIconUrl } = useWorkspaceIcon();

  const handleNotificationCountChange = useCallback((count: number) => {
    setUnreadNotificationCount(count);
  }, []);

  // Sync state with URL changes (browser back/forward)
  useEffect(() => {
    setSelected(getSelectedTab());
  }, [location.pathname]);

  // Background notification polling
  useEffect(() => {
    const checkForNotifications = async () => {
      try {
        const tokenValid = await EnhancedTokenManager.ensureValidToken();

        if (!tokenValid) {
          console.warn("⚠️ Token refresh failed");
          return;
        }

        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) return;

        const response = await fetch(API_URLS.GET_NOTIFICATIONS, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.notifications) {
            const unreadNotifs = data.notifications.filter(
              (notif: any) => !notif.is_read,
            );

            const previousCount = unreadNotificationCount;

            if (unreadNotifs.length > previousCount) {
              console.log(
                `📬 NEW notifications: ${previousCount} → ${unreadNotifs.length}`,
              );

              const newNotifications = unreadNotifs.slice(previousCount);
              const hasCardSharedOrReshare = newNotifications.some(
                (n: any) =>
                  n.type === "card_shared" || n.type === "feedback_reshared",
              );
              const hasDeveloperAction = newNotifications.some(
                (n: any) => n.type === "developer_action",
              );

              if (hasCardSharedOrReshare) {
                const newCardIds = newNotifications
                  .filter(
                    (n: any) =>
                      n.type === "card_shared" ||
                      n.type === "feedback_reshared",
                  )
                  .map((n: any) => n.card_id)
                  .filter(Boolean);

                setPendingNotificationCardIds(new Set(newCardIds));
                console.log(
                  "⏳ Waiting for cards to be processed before showing dot...",
                );
              }

              if (hasDeveloperAction) {
                console.log(
                  "✅ Developer action notification - showing dot immediately",
                );
                setShowNotificationDot(true);
              }
            }

            setUnreadNotificationCount(unreadNotifs.length);
          }
        }
      } catch (error) {
        console.error("Background notification check failed:", error);
      }
    };

    checkForNotifications();
    const intervalId = setInterval(checkForNotifications, 10000);

    return () => clearInterval(intervalId);
  }, [unreadNotificationCount]);

  useEffect(() => {
    const handleCardProcessed = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId } = event.detail;
      console.log("✅ Card processed, checking if dot should show:", cardId);

      if (pendingNotificationCardIds.has(cardId)) {
        console.log("✅ Pending card processed - showing notification dot");
        setShowNotificationDot(true);

        setPendingNotificationCardIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(cardId);
          return newSet;
        });
      }
    };

    window.addEventListener(
      "card-processed-notification",
      handleCardProcessed as EventListener,
    );

    return () => {
      window.removeEventListener(
        "card-processed-notification",
        handleCardProcessed as EventListener,
      );
    };
  }, [pendingNotificationCardIds]);

  const refreshWorkspaceIcon = useCallback(
    async (workspaceId: number, immediateUrl?: string) => {
      console.log("🔄 Refreshing workspace icon...");

      if (immediateUrl) {
        console.log("⚡ Using immediate workspace icon URL:", immediateUrl);

        setWorkspaceIconUrl((prevUrl) => {
          if (prevUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(prevUrl);
          }
          return prevUrl;
        });

        setWorkspaceIconUrl(immediateUrl);
        setIconLoadError(false);
        setIconLoading(false);
        console.log("✅ Workspace icon updated immediately");
        return;
      }

      setWorkspaceIconUrl((prevUrl) => {
        if (prevUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(prevUrl);
        }
        return prevUrl;
      });

      const newIconUrl = await fetchWorkspaceIcon(workspaceId, true);

      if (newIconUrl && newIconUrl !== workspaceLogo) {
        setWorkspaceIconUrl(newIconUrl);
        setIconLoadError(false);
        console.log("✅ Workspace icon refreshed successfully:", newIconUrl);
      } else {
        console.warn("⚠️ Failed to refresh workspace icon, using default");
        setWorkspaceIconUrl(workspaceLogo);
      }
    },
    [],
  );

  useEffect(() => {
    if (iconUpdateTrigger > 0) {
      console.log("📢 Icon update trigger received:", iconUpdateTrigger);

      const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
      if (workspaceId) {
        refreshWorkspaceIcon(workspaceId, immediateIconUrl || undefined);
      }
    }
  }, [iconUpdateTrigger, immediateIconUrl]);

  // Priority icon loading
  useEffect(() => {
    let isMounted = true;

    const loadIconImmediately = async () => {
      console.log("🚀 PRIORITY: Loading workspace icon immediately...");
      setIconLoading(true);

      let workspaceId: number | null =
        EnhancedTokenManager.getWorkspaceIdForCurrentUser();

      if (!workspaceId) {
        const cachedWorkspace =
          EnhancedTokenManager.getWorkspaceDataForCurrentUser();
        workspaceId = cachedWorkspace?.id ?? null;
      }

      if (!workspaceId) {
        const userProfile = EnhancedTokenManager.getUserProfile();
        workspaceId = userProfile?.workspace?.id ?? null;
      }

      if (workspaceId && isMounted) {
        console.log(
          `🎨 PRIORITY: Fetching icon for workspace ID: ${workspaceId}`,
        );
        const iconUrl = await fetchWorkspaceIcon(workspaceId);

        if (isMounted) {
          if (iconUrl && iconUrl !== workspaceLogo) {
            setWorkspaceIconUrl(iconUrl);
            setIconLoadError(false);
            setIconLoading(false);
          } else {
            setWorkspaceIconUrl(workspaceLogo);
            setIconLoadError(true);
            setIconLoading(false);
          }
        }
      } else {
        if (isMounted) {
          setIconLoading(true);
        }
      }
    };

    loadIconImmediately();

    return () => {
      isMounted = false;
    };
  }, []);

  // Main data loading
  useEffect(() => {
    let isMounted = true;

    const loadWorkspaceData = async () => {
      try {
        console.log("🔄 Loading workspace data...");

        const isSubdomain = SubdomainManager.isOnWorkspaceSubdomain();

        if (isSubdomain) {
          const tokensRestored = await SubdomainManager.restoreTokensFromUrl();

          if (tokensRestored) {
            await new Promise((resolve) => setTimeout(resolve, 1500));
          } else {
            await new Promise((resolve) => setTimeout(resolve, 800));
          }
        } else {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        if (!isMounted) return;

        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          if (isMounted) setWorkspaceDataLoaded(true);
          return;
        }

        let workspaceData = null;
        let userProfile = null;

        try {
          const result =
            await EnhancedTokenManager.fetchAndCheckUserProfile(accessToken);

          if (result.success && result.profile) {
            userProfile = result.profile;

            if (userProfile.workspace) {
              workspaceData = userProfile.workspace;
              EnhancedTokenManager.setWorkspaceDataForUser(
                userProfile.id,
                userProfile.workspace,
              );
            }
          } else {
            workspaceData =
              EnhancedTokenManager.getWorkspaceDataForCurrentUser();
            userProfile = EnhancedTokenManager.getUserProfile();
          }
        } catch (error) {
          workspaceData = EnhancedTokenManager.getWorkspaceDataForCurrentUser();
          userProfile = EnhancedTokenManager.getUserProfile();
        }

        if (!workspaceData && userProfile?.onboarded) {
          try {
            const response = await fetch(API_URLS.GET_WORKSPACES, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            });

            if (response.ok) {
              const workspacesData = await response.json();

              if (
                workspacesData.workspaces &&
                workspacesData.workspaces.length > 0
              ) {
                const workspace = workspacesData.workspaces[0];

                workspaceData = {
                  id: workspace.id,
                  name: workspace.name,
                  subdomain: workspace.subdomain,
                  icon_url: workspace.icon_url,
                };

                if (userProfile) {
                  EnhancedTokenManager.setWorkspaceDataForUser(
                    userProfile.id,
                    workspaceData,
                  );
                }
              }
            }
          } catch (error) {
            console.error("❌ Failed to fetch workspaces:", error);
          }
        }

        const name =
          workspaceData?.name || userProfile?.workspace?.name || "Loading...";
        const subdomain =
          workspaceData?.subdomain ||
          userProfile?.workspace?.subdomain ||
          SubdomainManager.getStoredWorkspaceSubdomain() ||
          "";
        const workspaceId = workspaceData?.id || userProfile?.workspace?.id;

        if (isMounted) {
          if (name && name !== "Loading...") {
            setWorkspaceName(name);
          }
          if (subdomain) {
            setWorkspaceSubdomain(subdomain);
          }

          if (workspaceId && !workspaceIconUrl) {
            const iconUrl = await fetchWorkspaceIcon(workspaceId);
            if (isMounted) {
              if (iconUrl && iconUrl !== workspaceLogo) {
                setWorkspaceIconUrl(iconUrl);
                setIconLoadError(false);
                setIconLoading(false);
              } else {
                setWorkspaceIconUrl(workspaceLogo);
                setIconLoadError(true);
                setIconLoading(false);
              }
            }
          } else if (workspaceIconUrl) {
            setIconLoading(false);
          }

          setWorkspaceDataLoaded(true);
        }
      } catch (error) {
        console.error("❌ Error loading workspace data:", error);

        if (isMounted) {
          setWorkspaceName("Workspace");
          if (!workspaceIconUrl) {
            setWorkspaceIconUrl(workspaceLogo);
            setIconLoadError(true);
          }
          setIconLoading(false);
          setWorkspaceDataLoaded(true);
        }
      }
    };

    loadWorkspaceData();

    return () => {
      isMounted = false;
      setWorkspaceIconUrl((prevUrl) => {
        if (prevUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(prevUrl);
        }
        return prevUrl;
      });
    };
  }, []);

  // Periodic token refresh check
  useEffect(() => {
    const tokenCheckInterval = setInterval(
      async () => {
        try {
          const isExpired = EnhancedTokenManager.isTokenExpired();

          if (isExpired) {
            console.log("🔄 Token expired, refreshing in background...");
            const refreshed = await EnhancedTokenManager.refreshAccessToken();

            if (refreshed) {
              console.log("✅ Token auto-refreshed successfully");
            } else {
              console.warn(
                "⚠️ Token refresh failed, user may need to re-login",
              );
            }
          }
        } catch (error) {
          console.error("❌ Error in token check:", error);
        }
      },
      10 * 60 * 1000,
    );

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, []);

  // Check token on window focus
  useEffect(() => {
    const handleFocus = async () => {
      try {
        const isExpired = EnhancedTokenManager.isTokenExpired();

        if (isExpired) {
          console.log("🔄 Tab focused with expired token, refreshing...");
          const refreshed = await EnhancedTokenManager.ensureValidToken();

          if (refreshed) {
            console.log("✅ Token refreshed on focus");
          }
        }
      } catch (error) {
        console.error("❌ Error refreshing token on focus:", error);
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Notification event listeners
  useEffect(() => {
    console.log("🎧 Setting up notification event listeners in Dashboard");

    const handleOpenSharedCard = (event: Event) => {
      console.log("📬 Dashboard: SHARED card event received (raw)", event);

      if (!(event instanceof CustomEvent)) {
        console.error("❌ Event is not a CustomEvent!", event);
        return;
      }

      const eventData = event.detail;
      console.log(
        "📬 Dashboard: Shared card notification received:",
        eventData,
      );

      setShowNotificationPopup(false);
      navigate("/dashboard", { replace: true }); // ✅ Use replace
      setSelected("home");
      setSidebarHidden(false);

      if (eventData.instant && eventData.cardData) {
        console.log(
          "⚡⚡ INSTANT OPEN: Injecting pre-loaded card data directly into state",
        );

        // Only write if NotificationPopup hasn't already set a complete entry
        const existingEntry = sessionStorage.getItem(`instant_card_${eventData.cardId}`);
        if (!existingEntry) {
          const cardDataRaw = eventData.cardData as any;
          const tempCardData = {
            cardId: eventData.cardId,
            cardName: eventData.cardData.name,
            feedbackCount: eventData.cardData.feedbackCount,
            previewImage: eventData.cardData.previewImage,
            createdAt: eventData.cardData.createdAt,
            instant: true,
            fullFeedbacks: cardDataRaw.fullFeedbacks || [],
            developers: cardDataRaw.developers || [],
          };

          sessionStorage.setItem(
            `instant_card_${eventData.cardId}`,
            JSON.stringify(tempCardData),
          );
        }
      }

      console.log("✅ Setting pendingNotificationEvent for SHARED card");
      setPendingNotificationEvent({
        type: "shared",
        data: eventData,
      });
    };

    const handleOpenCreatedCard = (event: Event) => {
      console.log("🔧 Dashboard: CREATED card event received (raw)", event);

      if (!(event instanceof CustomEvent)) {
        console.error("❌ Event is not a CustomEvent!", event);
        return;
      }

      const eventData = event.detail;
      console.log(
        "🔧 Dashboard: Created card notification received:",
        eventData,
      );

      setShowNotificationPopup(false);
      navigate("/dashboard", { replace: true }); // ✅ Use replace
      setSelected("home");
      setSidebarHidden(false);

      console.log("✅ Setting pendingNotificationEvent for CREATED card");
      setPendingNotificationEvent({
        type: "created",
        data: eventData,
      });
    };

    window.addEventListener(
      "open-shared-card-from-notification",
      handleOpenSharedCard,
    );
    window.addEventListener(
      "open-created-card-from-notification",
      handleOpenCreatedCard,
    );

    console.log("✅ Event listeners registered");

    return () => {
      console.log("🧹 Cleaning up notification event listeners");
      window.removeEventListener(
        "open-shared-card-from-notification",
        handleOpenSharedCard,
      );
      window.removeEventListener(
        "open-created-card-from-notification",
        handleOpenCreatedCard,
      );
    };
  }, [navigate]);

  useEffect(() => {
    if (pendingNotificationEvent) {
      console.log(
        "🎯 Dashboard: pendingNotificationEvent updated:",
        pendingNotificationEvent,
      );
    }
  }, [pendingNotificationEvent]);

  const handleHideSidebar = useCallback((hide: boolean) => {
    console.log("DashboardScreen - handleHideSidebar called with:", hide);
    setSidebarHidden(hide);
  }, []);

  const handleHomeClick = useCallback(() => {
    console.log("🏠 Home icon clicked - returning to home view");
    navigate("/dashboard", { replace: true }); // ✅ Use replace to avoid history pollution
    setSelected("home");
    setSidebarHidden(false);
    setHomeResetTrigger((prev) => prev + 1);
    setTimeout(() => {
      setHomeResetTrigger(0);
    }, 500);
  }, [navigate]);

  const handleIconError = () => {
    console.error("❌ Error loading workspace icon, falling back to default");
    if (!iconLoadError) {
      setIconLoadError(true);
      setWorkspaceIconUrl(workspaceLogo);
      setIconLoading(false);
    }
  };

  const handleNotificationIconClick = () => {
    setShowNotificationPopup(true);
    setShowNotificationDot(false);
    console.log("🔔 Notification popup opened - clearing dot");
  };

  const handleSettingsClick = () => {
    navigate("/dashboard/settings", { replace: true }); // ✅ Use replace
    setSelected("settings");
  };

  const isOnWorkspace = !!useMatch("/dashboard/workspace/:cardId");
  const isOnFeedback = !!useMatch("/dashboard/feedback/:cardId");
  const isOnUpload = !!useMatch("/dashboard/upload/:cardId");
  const isOnSettings = !!useMatch("/dashboard/settings/*");
  const isOnSubPage = isOnWorkspace || isOnFeedback || isOnUpload || isOnSettings;

  const outletContext = {
    onHideSidebar: handleHideSidebar,
    pendingNotificationEvent,
    onNotificationEventProcessed: () => setPendingNotificationEvent(null),
    homeResetTrigger,
  };

  const shouldHideSidebar = hideSidebar || sidebarHidden;

  return (
    <div className="bg-white relative h-screen overflow-hidden">
      <div className="flex h-full">
        {!shouldHideSidebar && (
          <>
            <div className="w-20 h-screen">
              <div className="flex flex-col justify-between h-full items-center">
                <div>
                  <div className="relative group">
                    <div className="w-[46px] h-[46px] mx-auto mt-6 rounded-lg overflow-hidden bg-white shadow-sm border border-gray-100 cursor-pointer">
                      {iconLoading && !workspaceIconUrl ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-50">
                          <Shimmer className="w-full h-full rounded-lg" />
                        </div>
                      ) : (
                        <img
                          src={workspaceIconUrl || workspaceLogo}
                          alt="Workspace Icon"
                          className="w-full h-full object-cover"
                          onError={handleIconError}
                          crossOrigin={
                            workspaceIconUrl?.startsWith("data:") ||
                            workspaceIconUrl?.startsWith("blob:")
                              ? undefined
                              : "anonymous"
                          }
                        />
                      )}
                    </div>

                    <div className="absolute left-full ml-2 top-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                      {workspaceDataLoaded ? (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-4 min-w-[280px]">
                          <div className="flex items-center mb-2">
                            <span className="text-[12px] 3xl:text-[14px] font-normal font-inter text-[#7F8390]">
                              Your Workspace
                            </span>
                          </div>
                          <div className="border-t border-[#EFF0F5] mb-3"></div>
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-xl 3xl:text-[24px] font-semibold text-[#1E1F26] font-inter">
                              {workspaceName ? workspaceName : "Loading..."}
                            </div>
                            {workspaceDataLoaded && workspaceName && (
                              <Check className="w-4 h-4 2xl:w-5 2xl:h-5 text-[#11A224]" />
                            )}
                          </div>
                          <div className="text-[12px] 3xl:text-[14px] font-normal text-[#7F8390] font-inter">
                            {workspaceSubdomain
                              ? workspaceSubdomain
                              : "No subdomain"}
                          </div>
                        </div>
                      ) : (
                        <WorkspaceTooltipShimmer />
                      )}
                    </div>
                  </div>
                  <div className="w-[28px] h-[2px] bg-[#E4E4E4] mx-auto mt-4"></div>

                  <div
                    className={`h-[48px] w-[48px] flex items-center justify-center rounded-[10px] cursor-pointer mx-auto mt-4 hover:bg-[#ECF6FF] ${
                      selected === "home" ? "bg-[#ECF6FF] " : ""
                    }`}
                    onClick={handleHomeClick}
                  >
                    <HomeIcon
                      className={`w-[24px] h-[24px] ${
                        selected === "home"
                          ? "text-[#4A6CF7]"
                          : "text-[#61646F]"
                      }`}
                    />
                  </div>

                  <div
                    className={`relative h-[48px] w-[48px] flex items-center justify-center rounded-[10px] cursor-pointer mx-auto mt-4 hover:bg-[#ECF6FF] ${
                      selected === "notifications" ? "bg-[#ECF6FF]" : ""
                    }`}
                    onClick={handleNotificationIconClick}
                  >
                    <NotificationIcon
                      className={`w-[24px] h-[24px] ${
                        selected === "notifications"
                          ? "text-[#4A6CF7]"
                          : "text-[#61646F]"
                      }`}
                    />
                    {showNotificationDot && (
                      <div className="absolute top-2 right-2 w-2 h-2 bg-orange-500 rounded-full"></div>
                    )}
                  </div>

                  <div
                    className={`h-[48px] w-[48px] flex items-center justify-center rounded-[10px] cursor-pointer mx-auto mt-4 hover:bg-[#ECF6FF] ${
                      selected === "settings" ? "bg-[#ECF6FF]" : ""
                    }`}
                    onClick={handleSettingsClick}
                  >
                    <SettingIcon
                      className={`w-[24px] h-[24px] ${
                        selected === "settings"
                          ? "text-[#4A6CF7]"
                          : "text-[#61646F]"
                      }`}
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <img
                    src={bottomlogo}
                    alt="Bottom"
                    className="w-[26px] h-[26px] mx-auto"
                  />
                </div>
              </div>
            </div>

            <div className="w-[1px] h-screen bg-[#E4E4E4]"></div>
          </>
        )}

        <div
          className={`flex-1 relative h-full ${
            shouldHideSidebar ? "w-full" : ""
          }`}
        >
          {/* HomeScreen is always mounted — state (cards, tabs) survives navigation */}
          <div style={{ display: isOnSubPage ? "none" : "block", height: "100%", overflowY: "auto" }}>
            <HomeScreen
              onHideSidebar={handleHideSidebar}
              resetToHome={homeResetTrigger > 0}
              pendingNotificationEvent={pendingNotificationEvent}
              onNotificationEventProcessed={() => setPendingNotificationEvent(null)}
            />
          </div>

          {/* Sub-pages (workspace / feedback / upload / settings) */}
          {isOnSubPage && (
            <div style={{ position: "absolute", inset: 0, background: "#fff", zIndex: 10 }}>
              <Outlet context={outletContext} />
            </div>
          )}
        </div>
      </div>

      <NotificationPopup
        isOpen={showNotificationPopup}
        onClose={() => {
          setShowNotificationPopup(false);
        }}
        onNotificationCountChange={handleNotificationCountChange}
      />
    </div>
  );
};

export default DashboardScreen;
